'use client';

import type { ClassValue } from 'clsx';

import { DateTimeSerializableFromString } from '@vs-shop/schemas/DateTimeSerializableFromString';
import { clsxm } from '@vs-shop/utils';
import { Schema } from 'effect';
import { CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import { useMediaQuery } from 'usehooks-ts';

import { getDefaultCurrency } from '../../../../../config/currency';

import ChartTooltip from '../../../chart/ChartTooltip';
import { intlFormatDate, intlFormatMoney } from '../../../../server/intl/intlFormat';

import type { ProductVariantPriceHistoryItem } from '../../../../../effect/schema/openapi/backend';

type Props = {
  data: readonly ProductVariantPriceHistoryItem[];
  className?: ClassValue;
};

export default function ProductPriceHistoryModalChart({ data, className }: Props) {
  const isScreenTabletAndMore = useMediaQuery('(min-width: 640px)');

  const currency = getDefaultCurrency();

  const chartMargin = isScreenTabletAndMore
    ? { top: 40, right: 50, bottom: 10, left: 50 }
    : { top: 20, right: 20, bottom: 0, left: 20 };

  const mutableChartData = Array.from(data).map((item) => ({
    date: item.date.value,
    price: item.price,
  }));

  return (
    <div className="custom-recharts rounded-lg bg-background">
      <LineChart
        responsive
        data={mutableChartData}
        margin={chartMargin}
        className={clsxm('aspect-4/3 w-full tab:aspect-video', className)}
      >
        <YAxis
          dataKey="price"
          tickSize={8}
          tickMargin={10}
          axisLine={true}
          tickLine={true}
          domain={[
            (dataMin: number) => dataMin,
            (dataMax: number) => dataMax,
            // (dataMin: number) => Math.floor(dataMin * 0.95),
            // (dataMax: number) => Math.ceil(dataMax * 1.05),
          ]}
          tickFormatter={(price: number) => intlFormatMoney({ value: { amount: price, currency } })}
        />

        <XAxis
          dataKey="date"
          tickSize={8}
          tickMargin={10}
          axisLine={true}
          tickLine={true}
          tickFormatter={(dateString: string) =>
            intlFormatDate(Schema.decodeSync(DateTimeSerializableFromString)(dateString))
          }
          textAnchor="end"
          angle={-45}
          height={80}
          // interval={0}
        />

        <Line
          type="linear"
          dataKey="price"
          isAnimationActive={false}
          stroke="var(--color-primary)"
          strokeWidth={2}
        />

        <Tooltip content={<ChartTooltip />} />
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-chart-cartesian-grid)" />
      </LineChart>
    </div>
  );
}
